import type { RefObject } from "react";
import { useTranslation } from "react-i18next";
import {
  DETAILS_MIN_WIDTH,
  RAIL_MIN_WIDTH,
  updateTablesLayout,
} from "./tablesLayout";
import { usePointerDrag } from "../../cardResize/usePointerDrag";

/**
 * Дръжката на границата между масите вляво и поръчаното вдясно (десктоп,
 * „Списък"): влачене — ширината на релсата, двоен клик — по подразбиране.
 * Релсата не става по-тясна от `RAIL_MIN_WIDTH`, а дясната част — от
 * `DETAILS_MIN_WIDTH`. НЕВИДИМА — без хапче и линия (решение 21.10.2026, както
 * дръжките на кутийките), подсказва само курсорът.
 */
export const RailResizeHandle = ({ railRef }: { railRef: RefObject<HTMLElement | null> }) => {
  const { t } = useTranslation();
  const { handleProps } = usePointerDrag({
    onStart: () => {
      const rail = railRef.current;
      const row = rail?.parentElement;
      if (!rail || !row) return null;
      return {
        width: rail.getBoundingClientRect().width,
        maxWidth: Math.max(RAIL_MIN_WIDTH, row.clientWidth - DETAILS_MIN_WIDTH),
      };
    },
    onMove: (start, deltaX) =>
      updateTablesLayout(
        { railWidth: Math.round(Math.max(RAIL_MIN_WIDTH, Math.min(start.maxWidth, start.width + deltaX))) },
        { persist: false },
      ),
    onEnd: () => updateTablesLayout({}, { persist: true }),
  });

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={t("ordering.tablesLayout.resizeRail")}
      title={t("ordering.tablesLayout.resizeRail")}
      onDoubleClick={() => updateTablesLayout({ railWidth: null }, { persist: true })}
      {...handleProps}
      className="absolute inset-y-0 -right-2 z-10 w-4 cursor-col-resize touch-none select-none"
    />
  );
};
